import { TFunction } from 'i18next'

import { AvailableEntity } from '../types'
import { SchemaAttributes, SchemaRelation } from '@/Data/types'
import {
  getSchemaAttributes,
  getVisibleAttributes,
} from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getLocalizedTitle } from '@/utils/schemaLocalization'

interface BuildAvailableEntitiesParams {
  entityName: string
  baseSchemaAttributes: SchemaAttributes | null
  schemaRelations: SchemaRelation[] | null
  t: TFunction
}

export function buildAvailableEntities({
  entityName,
  baseSchemaAttributes,
  schemaRelations,
  t,
}: BuildAvailableEntitiesParams): AvailableEntity[] {
  const entities: AvailableEntity[] = [
    {
      value: entityName,
      label: getBaseEntityLabel(entityName, t),
      referenceField: undefined,
    },
  ]

  if (baseSchemaAttributes) {
    const referencedEntities = extractReferencedEntities(
      entityName,
      baseSchemaAttributes,
    )
    entities.push(...referencedEntities)
  }

  const relationEntities = extractRelationEntities(
    entities,
    schemaRelations || [],
  )
  entities.push(...relationEntities)

  return entities
}

/**
 * Resolve the display label of the filter's base entity.
 *
 * Callers pass `entityName` either in DataClass casing ("Person") or lowercased
 * ("person"), so the name is capitalized before looking up the translation key.
 * Falls back to the capitalized entity name when no key is mapped.
 */
function getBaseEntityLabel(entityName: string, t: TFunction): string {
  const capitalized = entityName.charAt(0).toUpperCase() + entityName.slice(1)
  const labelKey = getEntityLabelKey(capitalized)
  const fallback = capitalized

  if (!labelKey) return fallback

  const translated = t(labelKey, { count: 1, defaultValue: fallback })
  return translated.charAt(0).toUpperCase() + translated.slice(1)
}

function extractReferencedEntities(
  entityName: string,
  baseSchemaAttributes: SchemaAttributes,
): AvailableEntity[] {
  const schemaName = getSchemaName(entityName)
  const { schemaGroups, groupsOrder } = getSchemaAttributes(schemaName)
  const { visibleAttributes } = getVisibleAttributes(
    schemaGroups,
    groupsOrder,
    baseSchemaAttributes,
  )

  return Object.entries(baseSchemaAttributes)
    .filter(([fieldName, attr]) => {
      if (attr.type !== 'reference' || !attr.referenceTo) return false

      if (
        visibleAttributes.length > 0 &&
        !visibleAttributes.includes(fieldName)
      ) {
        return false
      }

      return true
    })
    .map(([fieldName, attr]) => ({
      value: attr.referenceTo!.toLowerCase(),
      label: attr.title || attr.referenceTo!,
      referenceField: fieldName,
      relationType: 'many-to-one' as const,
    }))
    .filter(
      (entity, index, self) =>
        index ===
        self.findIndex(
          (e) =>
            e.value === entity.value &&
            e.referenceField === entity.referenceField,
        ),
    )
    .sort((a, b) => a.label.localeCompare(b.label))
}

function extractRelationEntities(
  existingEntities: AvailableEntity[],
  schemaRelations: SchemaRelation[],
): AvailableEntity[] {
  return schemaRelations
    .filter(
      (relation) =>
        !existingEntities.some(
          (e) =>
            e.value === relation.entityName &&
            e.referenceField === relation.relationPath,
        ),
    )
    .map((relation) => {
      const fallback =
        relation.entityName.charAt(0).toUpperCase() +
        relation.entityName.slice(1)
      const label = getLocalizedTitle(relation, fallback)

      return {
        value: relation.entityName,
        label,
        referenceField: relation.relationPath,
        relationType: relation.relationType,
      }
    })
}
